import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { Logo } from '@/components/ui/Logo'
import { readPendingInviteToken } from '@/lib/pendingInvite'
import { readPendingInstructorInviteToken } from '@/lib/pendingInstructorInvite'
import { readPendingAdminInviteToken } from '@/lib/pendingAdminInvite'
import { resolveAuthCallbackDestination } from '@/lib/authCallbackRouting'

export default function AuthCallback() {
  const navigate = useNavigate()
  const [confirmed, setConfirmed] = useState(false)

  useEffect(() => {
    // Implicit flow: type=recovery in hash (old Supabase emails)
    const hash = new URLSearchParams(window.location.hash.replace('#', ''))
    const query = new URLSearchParams(window.location.search)
    const urlType = hash.get('type') || query.get('type') || ''

    if (urlType === 'recovery') {
      navigate('/auth/reset-password', { replace: true })
      return
    }

    // PKCE flow: subscribe to auth events
    // onAuthStateChange always fires INITIAL_SESSION first, then subsequent events
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        navigate('/auth/reset-password', { replace: true })
      } else if (event === 'SIGNED_IN' || (event === 'INITIAL_SESSION' && session)) {
        const isEmailConfirmation = !!localStorage.getItem('dt_signup_email')
        if (isEmailConfirmation) {
          // New user just confirmed their email → sign out so they log in properly
          localStorage.removeItem('dt_signup_role')
          localStorage.removeItem('dt_signup_email')
          supabase.auth.signOut()
          setConfirmed(true)
        } else if (session?.user) {
          // Google OAuth login → look up profile role and send to portal
          const urlRole = new URLSearchParams(window.location.search).get('role')
          supabase
            .from('profiles')
            .select('primary_role')
            .eq('id', session.user.id)
            .single()
            .then(({ data }) => {
              const role = data?.primary_role
              // Same storage-based handoff Login.tsx uses for the email/password path —
              // a URL param cannot survive the OAuth provider round trip either.
              const pendingInstructorInviteToken = readPendingInstructorInviteToken()
              const pendingStudentInviteToken = readPendingInviteToken()
              const dest = resolveAuthCallbackDestination({ role, pendingInstructorInviteToken, pendingStudentInviteToken, urlRole })
              navigate(dest, { replace: true })
            })
        }
      }
    })

    return () => subscription.unsubscribe()
  }, [navigate])

  if (confirmed) {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center',
        justifyContent: 'center', background: '#FAF6EF', padding: '24px 16px',
      }}>
        <div style={{ width: '100%', maxWidth: 420, textAlign: 'center' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
            <Logo size={60} />
          </div>

          <div style={{
            width: 64, height: 64, borderRadius: '50%',
            background: 'rgba(15,124,108,0.12)', border: '2px solid #0F7C6C',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 20px',
          }}>
            <svg width='28' height='28' viewBox='0 0 24 24' fill='none'>
              <path d='M5 13l4 4L19 7' stroke='#0F7C6C' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round'/>
            </svg>
          </div>

          <div style={{ font: '600 22px Lora,serif', color: '#1B2A4A', marginBottom: 8 }}>
            Email confirmed!
          </div>
          <div style={{ font: '400 14px/1.6 Inter,sans-serif', color: 'rgba(27,42,74,0.6)', marginBottom: 28 }}>
            Your email address has been verified. You're all set — go ahead and log in.
          </div>

          <button
            onClick={() => {
              const pendingAdminInviteToken = readPendingAdminInviteToken()
              navigate(pendingAdminInviteToken ? '/admin/login?redirect=/accept-admin-invite' : '/login', { replace: true })
            }}
            className='btn-primary'
            style={{ maxWidth: 280, margin: '0 auto', display: 'block' }}
          >
            Go to login →
          </button>
        </div>
      </div>
    )
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#FAF6EF' }}>
      <div style={{ textAlign: 'center' }}>
        <Logo size={52} />
        <div style={{ font: '400 13px Inter,sans-serif', color: 'rgba(27,42,74,0.45)', marginTop: 16 }}>Verifying…</div>
      </div>
    </div>
  )
}
